// modified from line-intersect js
export const COLINEAR = "colinear";
export const PARALLEL = "parallel";
export const NONE = "none";
export const INTERSECTING = "intersecting";

/**
 * @typedef {import("@/types/MathTypes.mjs").Coordinates} Coordinates
 * @typedef {import("@/types/MathTypes.mjs").Ray} Ray
 * 
 * @typedef {{
 *  type: COLINEAR | INTERSECTING | NONE | PARALLEL,
 *  point?: Coordinates
 * }} IntersectionResult
 */

/**
 * Check how two line segments intersect eachother. Line segments are represented
 * as (x1, y1)-(x2, y2) and (x3, y3)-(x4, y4).
 *
 * @param {Ray} ray1
 * @param {Ray} ray2
 * @return {IntersectionResult} Object describing intersection that looks like
 */

// Tolerance for treating near-zero determinants as parallel/colinear. Rotation
// matrices for 180°/270° produce sin/cos values that are ~1e-16 off from 0,
// which leaves segments that should be exactly parallel with a tiny non-zero
// denom. Without this tolerance, the FloatingStageItem toolbar's bottom edge
// (horizontal) gets reported as intersecting the rotated selection's visual
// top edge (also horizontal) at uA=uB=0.5, triggering a spurious shift.
const PARALLEL_EPSILON = 1e-9;

const checkIntersection = (ray1, ray2) => {
  const [x1, y1] = ray1[0];
  const [x2, y2] = ray1[1];
  const [x3, y3] = ray2[0];
  const [x4, y4] = ray2[1];

  const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
  const numeA = (x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3);
  const numeB = (x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3);

  if (Math.abs(denom) < PARALLEL_EPSILON) {
    return {
      type:
        Math.abs(numeA) < PARALLEL_EPSILON && Math.abs(numeB) < PARALLEL_EPSILON
          ? COLINEAR
          : PARALLEL,
    };
  }

  const uA = numeA / denom;
  const uB = numeB / denom;

  if (uA >= 0 && uA <= 1 && uB >= 0 && uB <= 1) {
    return {
      type: INTERSECTING,
      point: [x1 + uA * (x2 - x1),  y1 + uA * (y2 - y1)]
    };
  }

  return { type: NONE };
};

export default checkIntersection;